import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/money';
import { resolveCustomFeeLabel } from './custom-fee-display';
import { getCurrentBillingWeek, getPriceDescriptionKey, getPercentageDescriptionKey } from './utils';
import { BillingEmailSection } from './BillingEmailSection';
import type { PricingItem } from './pricing-config';

interface PlanDetailsTabProps {
  accountPlan: any;
  isLoadingAccountPlan: boolean;
  pricingCategories: Record<string, PricingItem[]> | null;
}

const resolveItemLabel = (item: PricingItem, t: TFunction<'billing'>): string => {
  if (item.feeDescription) return resolveCustomFeeLabel(item.feeDescription, t);
  if (item.nameKey) return t(item.nameKey);
  return item.id;
};

const PricingItemRow = ({ item, t }: { item: PricingItem; t: TFunction<'billing'> }) => (
  <div>
    <div className="flex items-center justify-between px-4 py-2">
      <div className="flex items-center gap-2">
        <div className="h-2 w-2 rounded-full bg-green-500" />
        <span className="text-sm font-medium">{resolveItemLabel(item, t)}</span>
      </div>
      <div className="flex items-center gap-10">
        {item.price && (
          <div className="text-right">
            <div className="text-sm">{item.price}</div>
            <div className="text-muted-foreground text-xs">
              {t(
                getPriceDescriptionKey(
                  item.id,
                  item.feeDescription ? { isRecurring: item.isRecurring } : undefined,
                ),
              )}
            </div>
          </div>
        )}
        {item.percentage && (
          <div className="min-w-[180px] text-right">
            <div className="text-sm font-medium">{item.percentage}</div>
            <div className="text-muted-foreground text-xs">{t(getPercentageDescriptionKey(item.id))}</div>
          </div>
        )}
      </div>
    </div>
    <hr className="border-gray-200" />
  </div>
);

export function PlanDetailsTab({
  accountPlan,
  isLoadingAccountPlan,
  pricingCategories,
}: PlanDetailsTabProps) {
  const { t } = useTranslation('billing');
  const enabledItems = pricingCategories
    ? Object.values(pricingCategories).flatMap((items) => items.filter((item) => item.enabled))
    : [];

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-5">
            <div className="space-y-6 md:col-span-2">
              <div>
                <p className="text-muted-foreground text-sm font-medium">{t('planDetails.weeklyBaseFee')}</p>
                <p className="mt-1 text-lg font-semibold">
                  {accountPlan ? (
                    formatMoney(accountPlan.amountPlan, accountPlan.billingCurrency?.toUpperCase())
                  ) : (
                    <Skeleton className="h-6 w-24" />
                  )}
                  <span className="text-muted-foreground ml-1 text-sm font-normal">
                    {t('planDetails.perWeek')}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  {t('planDetails.currentBillingWeek')}
                </p>
                <p className="mt-1 text-sm">{getCurrentBillingWeek()}</p>
              </div>
            </div>

            <div className="md:col-span-3">
              <p className="text-muted-foreground mb-4 text-sm font-medium">
                {t('planDetails.transactionRates')}
              </p>
              {isLoadingAccountPlan ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : accountPlan && pricingCategories ? (
                <div className="space-y-0">
                  {enabledItems.map((item) => (
                    <PricingItemRow key={item.id} item={item} t={t} />
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <BillingEmailSection accountPlan={accountPlan} />
        </CardContent>
      </Card>
    </div>
  );
}
